import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { settingsApi } from '@/lib/api';
import { Settings, Save, Plus, X, Mail } from 'lucide-react';
import { toast } from 'sonner';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    church_name: "St. John's United Church of Christ",
    address: '1288 S. Indiana Ave, Crown Point, IN 46307',
    phone: '219-663-1608',
    email: 'cpuccsecretary@gmail.com',
    service_times: ['Sunday Worship: 10:00 AM'],
    facebook_url: 'https://www.facebook.com/stjohnsucc',
    youtube_url: '',
    easytithe_url: 'https://app.easytithe.com/app/giving/stjo6318650',
    easytithe_text_number: '2192555077',
    facebook_live_url: '',
    pastor_message_title: '',
    pastor_message_content: '',
    forwarding_emails: [],
    welcome_email_subject: "Welcome to St. John's United Church of Christ!",
    welcome_email_body: "Thank you for creating an account with St. John's United Church of Christ in Crown Point, Indiana. We're glad you're here!\n\nAs a visitor, you can submit prayer requests and contact us through our website. If you'd like access to the member directory and more features, please speak with a church administrator about upgrading your account.\n\nBlessings,\nSt. John's United Church of Christ\n1288 S. Indiana Ave, Crown Point, IN 46307\n219-663-1608"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const data = await settingsApi.get();
      if (data) setSettings({ ...settings, ...data });
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update(settings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Site Settings</h1>
          <p className="text-stone-600">Configure church website settings</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-amber-700 hover:bg-amber-800">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid gap-6">
        {/* Church Info */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold text-stone-800 mb-4 flex items-center gap-2">
              <Settings className="h-5 w-5" /> Church Information
            </h3>
            <div className="grid gap-4">
              <div>
                <Label>Church Name</Label>
                <Input
                  value={settings.church_name}
                  onChange={(e) => setSettings({ ...settings, church_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={settings.address}
                  onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={settings.phone}
                    onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={settings.email}
                    onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Service Times</Label>
                <div className="space-y-2 mt-1">
                  {(settings.service_times || []).map((time, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        value={time}
                        onChange={(e) => {
                          const updated = [...settings.service_times];
                          updated[index] = e.target.value;
                          setSettings({ ...settings, service_times: updated });
                        }}
                        placeholder="e.g., Sunday Worship: 10:00 AM"
                        data-testid={`service-time-input-${index}`}
                      />
                      {settings.service_times.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 shrink-0"
                          onClick={() => {
                            const updated = settings.service_times.filter((_, i) => i !== index);
                            setSettings({ ...settings, service_times: updated });
                          }}
                          data-testid={`remove-service-time-${index}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setSettings({ ...settings, service_times: [...(settings.service_times || []), ''] })}
                    data-testid="add-service-time-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Service Time
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Social Links */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold text-stone-800 mb-4">Social Media & Live Stream</h3>
            <div className="grid gap-4">
              <div>
                <Label>Facebook Page URL</Label>
                <Input
                  value={settings.facebook_url}
                  onChange={(e) => setSettings({ ...settings, facebook_url: e.target.value })}
                  placeholder="https://www.facebook.com/yourchurch"
                />
              </div>
              <div>
                <Label>YouTube Channel URL</Label>
                <Input
                  value={settings.youtube_url}
                  onChange={(e) => setSettings({ ...settings, youtube_url: e.target.value })}
                  placeholder="https://www.youtube.com/c/yourchurch"
                />
              </div>
              <div>
                <Label>Facebook Live Stream URL</Label>
                <Input
                  value={settings.facebook_live_url}
                  onChange={(e) => setSettings({ ...settings, facebook_live_url: e.target.value })}
                  placeholder="Paste the Facebook Live video URL when streaming"
                />
                <p className="text-sm text-stone-500 mt-1">Update this when you go live on Facebook</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Giving */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold text-stone-800 mb-4">Online Giving</h3>
            <div className="grid gap-4">
              <div>
                <Label>EasyTithe URL</Label>
                <Input
                  value={settings.easytithe_url}
                  onChange={(e) => setSettings({ ...settings, easytithe_url: e.target.value })}
                  placeholder="https://app.easytithe.com/app/giving/..."
                />
              </div>
              <div>
                <Label>Text-to-Give Number</Label>
                <Input
                  value={settings.easytithe_text_number}
                  onChange={(e) => setSettings({ ...settings, easytithe_text_number: e.target.value })}
                  placeholder="e.g., 2192555077"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pastor's Message */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold text-stone-800 mb-4">Pastor's Message (Homepage)</h3>
            <div className="grid gap-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={settings.pastor_message_title}
                  onChange={(e) => setSettings({ ...settings, pastor_message_title: e.target.value })}
                  placeholder="e.g., A Message from Pastor Wendell"
                />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea
                  value={settings.pastor_message_content}
                  onChange={(e) => setSettings({ ...settings, pastor_message_content: e.target.value })}
                  placeholder="Enter the pastor's message to display on the homepage..."
                  rows={8}
                />
              </div>
              <p className="text-sm text-stone-500">
                Leave empty to hide the pastor's message section on the homepage.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Email Notifications */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold text-stone-800 mb-4 flex items-center gap-2">
              <Mail className="h-5 w-5" /> Email Notifications
            </h3>
            <p className="text-sm text-stone-500 mb-4">
              Form submissions (contact, prayer requests) will be forwarded to these email addresses with an auto-reply sent to the submitter.
            </p>
            <div className="space-y-2">
              {(settings.forwarding_emails || []).map((email, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      const updated = [...settings.forwarding_emails];
                      updated[index] = e.target.value;
                      setSettings({ ...settings, forwarding_emails: updated });
                    }}
                    placeholder="email@example.com"
                    data-testid={`forwarding-email-input-${index}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 shrink-0"
                    onClick={() => {
                      const updated = settings.forwarding_emails.filter((_, i) => i !== index);
                      setSettings({ ...settings, forwarding_emails: updated });
                    }}
                    data-testid={`remove-forwarding-email-${index}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSettings({ ...settings, forwarding_emails: [...(settings.forwarding_emails || []), ''] })}
                data-testid="add-forwarding-email-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Email Address
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Welcome Email */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold text-stone-800 mb-4 flex items-center gap-2">
              <Mail className="h-5 w-5" /> Welcome Email
            </h3>
            <p className="text-sm text-stone-500 mb-4">
              Sent automatically to new users when they create an account.
            </p>
            <div className="space-y-4">
              <div>
                <Label>Subject</Label>
                <Input
                  value={settings.welcome_email_subject}
                  onChange={(e) => setSettings({ ...settings, welcome_email_subject: e.target.value })}
                  placeholder="Welcome to St. John's UCC!"
                  data-testid="welcome-email-subject"
                />
              </div>
              <div>
                <Label>Body</Label>
                <Textarea
                  value={settings.welcome_email_body}
                  onChange={(e) => setSettings({ ...settings, welcome_email_body: e.target.value })}
                  placeholder="Enter welcome email content..."
                  rows={8}
                  data-testid="welcome-email-body"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* External Management Links */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold text-stone-800 mb-4">External Management</h3>
            <div className="flex flex-wrap gap-3">
              <a href="https://wordpress.com/domains/manage/stjohnsucc.church/dns/stjohnsuccchurch.wordpress.com" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" type="button">WordPress DNS</Button>
              </a>
              <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" type="button">Resend Domains</Button>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Admin Password Info */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold text-stone-800 mb-4">Admin Access</h3>
            <p className="text-stone-600 text-sm">
              The admin password is set via environment variables on the server. 
              Contact your developer to change the admin password.
            </p>
            <p className="text-stone-500 text-sm mt-2">
              Current default: <code className="bg-stone-100 px-2 py-1 rounded">church123</code>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSettings;
